import axios from 'axios';
import { getEnviroment } from 'env';
import { defaultAxiosOptions } from '../common/DefaultAxiosOptions';
import { AccessToken } from '@okta/okta-auth-js';

export interface Group {
  id: string;
  name: string;
  selected: boolean;
}

export interface GroupsResult {
  groups: Group[];
  totalElements: number;
  hasMore: boolean;
}

interface GroupResponse {
  id: string;
  name: string;
  description?: string;
}

interface PaginatedGroupsResponse {
  content: GroupResponse[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const PAGE_SIZE = 100;

function getAllGroups(accessToken: AccessToken | undefined): Promise<GroupsResult> {
  return axios
    .get<PaginatedGroupsResponse>(
      `${getEnviroment().endpoints.irisGroups}?size=${PAGE_SIZE}`,
      defaultAxiosOptions(accessToken)
    )
    .then(response => ({
      groups: response.data.content.map(group => ({
        id: group.id,
        name: group.name,
        selected: false
      })),
      totalElements: response.data.totalElements,
      hasMore: response.data.totalElements > response.data.content.length
    }));
}

function getGroupsForAsset(
  accessToken: AccessToken | undefined,
  assetId: string
): Promise<Group[]> {
  return axios
    .get<PaginatedGroupsResponse>(
      `${getEnviroment().endpoints.irisGroups}?assetId=${assetId}&size=${PAGE_SIZE}`,
      defaultAxiosOptions(accessToken)
    )
    .then(response =>
      response.data.content.map(group => ({
        id: group.id,
        name: group.name,
        selected: true
      }))
    );
}

function getGroupsWithSelection(
  accessToken: AccessToken | undefined,
  assetId: string
): Promise<GroupsResult> {
  return Promise.all([
    getAllGroups(accessToken),
    getGroupsForAsset(accessToken, assetId)
  ]).then(([allGroupsResult, assetGroups]) => {
    const assetGroupIds = new Set(assetGroups.map(g => g.id));
    return {
      groups: allGroupsResult.groups.map(group => ({
        ...group,
        selected: assetGroupIds.has(group.id)
      })),
      totalElements: allGroupsResult.totalElements,
      hasMore: allGroupsResult.hasMore
    };
  });
}

function addAssetToGroups(
  accessToken: AccessToken | undefined,
  assetId: string,
  groupIds: string[]
): Promise<void> {
  if (groupIds.length === 0) {
    return Promise.resolve();
  }
  return axios
    .post(
      `${getEnviroment().endpoints.irisAssetGroups}/${assetId}/groups`,
      { groupIds },
      defaultAxiosOptions(accessToken)
    )
    .then(() => undefined);
}

function removeAssetFromGroups(
  accessToken: AccessToken | undefined,
  assetId: string,
  groupIds: string[]
): Promise<void> {
  if (groupIds.length === 0) {
    return Promise.resolve();
  }
  return axios
    .delete(
      `${getEnviroment().endpoints.irisAssetGroups}/${assetId}/groups`,
      {
        ...defaultAxiosOptions(accessToken),
        data: { groupIds }
      }
    )
    .then(() => undefined);
}

export const GroupApi = {
  getAllGroups,
  getGroupsForAsset,
  getGroupsWithSelection,
  addAssetToGroups,
  removeAssetFromGroups
};
